import {Alert, Attributes, Card, type ColorSchemeType, Flag} from 'platform/components';
import {Box, Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import {DateTimeFormat, useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import type {
  OrderStatusLogEntryResponseBody,
  OrderStatusLogFactsResponseBody,
  VehicleOrderDetailResponseBody,
} from '@dms/api/new-vehicle';
import {getNewVehicleEnumLabel} from '@dms/features/new-vehicle';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, Nullish, parseDate} from 'shared/web';

import {FactoryRecord} from './components/FactoryRecord/FactoryRecord';
import {StatusLogEffectLines} from './components/StatusLogEffectLines/StatusLogEffectLines';

const t = (key: string, defaultValue: string) => i18n.t(key, {defaultValue});

type FormatDate = ReturnType<typeof useDateTimeFormatter>;
type FactRow = {label: string; value: string; testId?: string};

/** Factory statements only: none of these dates is an actual build or the arrival of the car. */
const factoryLabels = {
  description: () =>
    t('entity.newVehicle.order.factoryStatusDescription', 'Factory status description'),
  recordedAt: () => t('entity.newVehicle.order.factoryRecordedAt', 'Factory status time'),
  plannedBuild: () => t('entity.newVehicle.order.plannedBuildDate', 'Planned build date (factory)'),
  scheduledBuild: () =>
    t('entity.newVehicle.order.scheduledBuildDate', 'Scheduled build date (factory)'),
  gateRelease: () => t('entity.newVehicle.order.gateReleaseDate', 'Gate release date (factory)'),
  dealerDelivery: () =>
    t('entity.newVehicle.order.dealerDeliveryDate', 'Dealer delivery date (factory)'),
  orderId: () => t('entity.newVehicle.order.factoryOrderId', 'Factory order ID'),
  orderVersion: () => t('entity.newVehicle.order.factoryOrderVersion', 'Factory order version'),
};

const keepPresent = (rows: {label: string; value: string | Nullish; testId?: string}[]) =>
  rows.filter((row): row is FactRow => isNotNilOrEmpty(row.value));

const getOrderFactoryRows = (order: VehicleOrderDetailResponseBody, formatDate: FormatDate) => {
  const date = (value: string | Nullish) =>
    isNil(value) ? null : formatDate('dateMedium', parseDate(value));
  return keepPresent([
    {
      label: factoryLabels.description(),
      value: order.factoryStatusDescription,
      testId: 'factory-description',
    },
    {
      label: factoryLabels.recordedAt(),
      testId: 'factory-recorded-at',
      value: isNil(order.factoryRecordedAt)
        ? null
        : formatDate('dateTimeShort', parseDate(order.factoryRecordedAt)),
    },
    {
      label: factoryLabels.plannedBuild(),
      value: date(order.plannedBuildDate),
      testId: 'factory-planned-build',
    },
    {
      label: factoryLabels.scheduledBuild(),
      value: date(order.scheduledBuildDate),
      testId: 'factory-scheduled-build',
    },
    {
      label: factoryLabels.gateRelease(),
      value: date(order.gateReleaseDate),
      testId: 'factory-gate-release',
    },
    {
      label: factoryLabels.dealerDelivery(),
      value: date(order.dealerDeliveryDate),
      testId: 'factory-dealer-delivery',
    },
    {label: factoryLabels.orderId(), value: order.factoryOrderId, testId: 'factory-order-id'},
    {
      label: factoryLabels.orderVersion(),
      value: order.factoryOrderVersion,
      testId: 'factory-order-version',
    },
  ]);
};

const getEntryFactRows = (
  facts: OrderStatusLogFactsResponseBody,
  holdChange: OrderStatusLogEntryResponseBody['holdChange'],
  formatDate: FormatDate
) => {
  const date = (value: string | null) =>
    isNil(value) ? null : formatDate('dateMedium', parseDate(value));
  const yesNo = (value: boolean | null) =>
    isNil(value) ? null : i18n.t(value ? 'general.labels.yes' : 'general.labels.no');
  return keepPresent([
    {label: i18n.t('entity.vehicle.labels.vin'), value: facts.vin},
    {label: i18n.t('entity.vehicle.labels.engineNumber'), value: facts.engineNumber},
    {
      label: t('entity.newVehicle.order.eta', 'Estimated arrival'),
      value: date(facts.estimatedDeliveryDate),
    },
    {label: factoryLabels.plannedBuild(), value: date(facts.plannedBuildDate)},
    {label: factoryLabels.scheduledBuild(), value: date(facts.scheduledBuildDate)},
    {label: factoryLabels.gateRelease(), value: date(facts.gateReleaseDate)},
    {label: factoryLabels.dealerDelivery(), value: date(facts.dealerDeliveryDate)},
    {label: factoryLabels.orderId(), value: facts.factoryOrderId},
    {label: factoryLabels.orderVersion(), value: facts.orderVersion},
    {
      label: t('entity.newVehicle.order.factoryCancelled', 'Cancelled by factory'),
      value: yesNo(facts.cancelled),
    },
    {
      label: t('entity.newVehicle.order.factoryContract', 'Customer contract'),
      value: yesNo(facts.contract),
    },
    {
      label: i18n.t('entity.newVehicle.order.factoryHold'),
      // A hold false that released nothing is no news; the importer may send it on every message.
      value: facts.hold === true || holdChange === 'released' ? yesNo(facts.hold) : null,
    },
    {label: i18n.t('entity.newVehicle.order.factoryHoldReason'), value: facts.holdReason},
    {
      label: i18n.t('entity.newVehicle.order.factoryHoldExpectedRelease'),
      value: date(facts.holdExpectedReleaseDate),
    },
  ]);
};

const getSignalLabel = (signal: NonNullable<OrderStatusLogEntryResponseBody['mappedSignal']>) =>
  match(signal)
    .with('delivered', () => t('entity.newVehicle.enums.orderSignal.delivered', 'Delivered'))
    .with('cancelled', () => t('entity.newVehicle.enums.orderSignal.cancelled', 'Cancelled'))
    .with('retail_sold', () => t('entity.newVehicle.enums.orderSignal.retailSold', 'Retail sold'))
    .exhaustive();

/** A stage row needs no marker; any other importer row did not move the order. */
const getTargetMarker = (
  entry: OrderStatusLogEntryResponseBody
): {label: string; colorScheme: ColorSchemeType} | null =>
  match(entry.targetKind)
    .with('signal', () => {
      const signal = t('entity.newVehicle.enums.statusLogTarget.signal', 'Signal');
      return {
        label: isNil(entry.mappedSignal)
          ? signal
          : `${signal} · ${getSignalLabel(entry.mappedSignal)}`,
        colorScheme: 'orange' as const,
      };
    })
    .with('informational', () => ({
      label: t('entity.newVehicle.enums.statusLogTarget.informational', 'Informational'),
      colorScheme: 'neutral' as const,
    }))
    .with('unmapped', () => ({
      label: t('entity.newVehicle.enums.statusLogTarget.unmapped', 'Unmapped status'),
      colorScheme: 'yellow' as const,
    }))
    .otherwise(() => null);

/** The newest importer receipt is the first one, because the API sends the log newest first. */
const getLatestReceiptMarker = (statusLog: OrderStatusLogEntryResponseBody[]) => {
  const latestReceipt = statusLog.find((entry) => entry.kind === 'integration');
  return isNil(latestReceipt) ? null : getTargetMarker(latestReceipt);
};

/** Importer feed, factory record and status history; renders nothing while there is none. */
export function DeliveryUpdatesCard(props: {order: VehicleOrderDetailResponseBody}) {
  const formatDate = useDateTimeFormatter();
  const factoryRows = getOrderFactoryRows(props.order, formatDate);
  const mappedStage = isNotNil(props.order.mappedStage)
    ? getNewVehicleEnumLabel('orderStatus', props.order.mappedStage, t)
    : null;
  const latestReceiptMarker = getLatestReceiptMarker(props.order.statusLog);
  const hasImporterFeed = isNotNilOrEmpty(props.order.rawStatus) || factoryRows.length > 0;

  if (!hasImporterFeed && props.order.statusLog.length === 0 && !props.order.hasFactoryRecord) {
    return null;
  }

  return (
    <Card
      title={t('entity.newVehicle.order.deliveryUpdates', 'Delivery updates')}
      subtitle={t(
        'entity.newVehicle.order.importerIntro',
        'Importer messages and manual delivery updates, newest first.'
      )}
      data-testid="new-vehicle-delivery-updates"
    >
      <VStack spacing={5}>
        <Show when={hasImporterFeed}>
          <VStack spacing={3}>
            <Attributes
              data-testid="new-vehicle-importer-feed"
              size="quarter"
              rows={[
                {
                  label: t('entity.newVehicle.order.rawImporterStatus', 'Raw importer status'),
                  value: props.order.rawStatus,
                },
                isNil(latestReceiptMarker)
                  ? {
                      label: t('entity.newVehicle.order.mappedStage', 'Mapped stage'),
                      value: mappedStage,
                    }
                  : {
                      label: t('entity.newVehicle.order.mappedStage', 'Mapped stage'),
                      testId: 'mapped-stage',
                      content: (
                        <HStack spacing={2} align="center">
                          <Text size="xSmall">{mappedStage ?? EMPTY_PLACEHOLDER}</Text>
                          <Flag
                            size="small"
                            colorScheme={latestReceiptMarker.colorScheme}
                            isSubtle
                            label={latestReceiptMarker.label}
                          />
                        </HStack>
                      ),
                    },
                ...factoryRows,
              ]}
            />
            <Show when={props.order.importerDiverges}>
              <Alert
                variant="warning"
                message={t(
                  'entity.newVehicle.order.importerDiverges',
                  'Importer status differs from the recorded stage'
                )}
              />
            </Show>
          </VStack>
        </Show>
        <Show when={props.order.hasFactoryRecord}>
          <FactoryRecord orderId={props.order.id} />
        </Show>
        <Show when={props.order.statusLog.length > 0}>
          <VStack spacing={2}>
            <HStack spacing={2} align="center">
              <Heading size={5}>
                {t('entity.newVehicle.order.statusHistory', 'Status history')}
              </Heading>
              <Flag
                size="small"
                colorScheme="neutral"
                isSubtle
                label={props.order.statusLog.length.toString()}
              />
            </HStack>
            <VStack>
              {props.order.statusLog.map((entry, index) => {
                const stage = isNotNil(entry.mappedStage)
                  ? getNewVehicleEnumLabel('orderStatus', entry.mappedStage, t)
                  : null;
                const text =
                  [entry.note, entry.rawStatus, stage].find(isNotNilOrEmpty) ?? EMPTY_PLACEHOLDER;
                const marker = getTargetMarker(entry);
                const facts = isNil(entry.facts)
                  ? []
                  : getEntryFactRows(entry.facts, entry.holdChange, formatDate);
                return (
                  <Box
                    // A timestamp alone repeats within importer batches; the entry content does not.
                    key={[entry.at, entry.kind, entry.rawStatus, entry.note].join('|')}
                    borderTop={index > 0 ? '1px solid' : undefined}
                    borderColor="general.separator"
                    paddingVertical={2}
                    data-testid={`new-vehicle-status-log-${index}`}
                  >
                    <HStack spacing={4} align="flex-start" justify="space-between">
                      <HStack spacing={4} align="flex-start" minWidth={0}>
                        <Box width={32} flexShrink={0}>
                          <Text size="small" color="secondary">
                            <DateTimeFormat
                              value={parseDate(entry.factoryRecordedAt ?? entry.at)}
                              format="dateTimeShort"
                            />
                          </Text>
                        </Box>
                        <VStack spacing={1} minWidth={0}>
                          <Text size="small">{text}</Text>
                          <Show
                            when={
                              isNotNilOrEmpty(entry.statusDescription)
                                ? entry.statusDescription
                                : null
                            }
                          >
                            {(description) => (
                              <Text size="xSmall" color="secondary">
                                {description}
                              </Text>
                            )}
                          </Show>
                          <StatusLogEffectLines entry={entry} />
                          <Show when={isNotNil(stage) && stage !== text ? stage : null}>
                            {(stage) => (
                              <Text size="xSmall" color="tertiary">
                                {stage}
                              </Text>
                            )}
                          </Show>
                          {facts.map((fact) => (
                            <Text key={fact.label} size="xSmall" color="tertiary">
                              {`${fact.label}: ${fact.value}`}
                            </Text>
                          ))}
                        </VStack>
                      </HStack>
                      <HStack spacing={2}>
                        <Show when={marker}>
                          {(marker) => (
                            <Flag
                              size="small"
                              colorScheme={marker.colorScheme}
                              isSubtle
                              label={marker.label}
                            />
                          )}
                        </Show>
                        <Flag
                          size="small"
                          colorScheme={entry.kind === 'integration' ? 'blue' : 'neutral'}
                          isSubtle
                          label={getNewVehicleEnumLabel('statusLogKind', entry.kind, t)}
                        />
                      </HStack>
                    </HStack>
                  </Box>
                );
              })}
            </VStack>
          </VStack>
        </Show>
      </VStack>
    </Card>
  );
}
